import React, { useState, useEffect } from "react";
import { arrayUnion, arrayRemove } from "firebase/firestore";
import {
  Typography,
  Button,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import { getDocs, collection, query, where, addDoc,doc,updateDoc } from "firebase/firestore";
import { firestore } from "../firebase.config";
import PageNavBar from '../components/PageNavBar';
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";
import { duplicateTeam, getTeamsForTenant, updateTeam, deleteTeam } from "../context/Api";
import Footer from "../components/Footer";

const FormTeam = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Create, 1 = Update
  const [userList, setUserList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [teamName, setTeamName] = useState("");
  const [teams, setTeams] = useState([]);
  
  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editSelectedUsers, setEditSelectedUsers] = useState([]);
  const [editLatitude, setEditLatitude] = useState("");
  const [editLongitude, setEditLongitude] = useState("");
  const [editSupervisorEmail, setEditSupervisorEmail] = useState("");
  
  // Duplicate dialog states
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [newSupervisorEmail, setNewSupervisorEmail] = useState("");
  const [duplicateError, setDuplicateError] = useState("");
  const [duplicateSuccess, setDuplicateSuccess] = useState("");
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  
  // Success/Error messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  
  const { userRole, tenantId, selectedTenantId } = useUserContext();

  //for team type
  const [teamType, setTeamType] = useState("colocated");
  const [memberCoordinates, setMemberCoordinates] = useState({});

  const [editTeamType, setEditTeamType] = useState("colocated");
  const [editMemberCoordinates, setEditMemberCoordinates] = useState({});

  const [radius, setRadius] = useState("");
  const [editRadius, setEditRadius] = useState("");

  const getTenantFilterID = () => {
    return userRole === "SuperAdmin"
      ? selectedTenantId || sessionStorage.getItem("selectedTenantId")
      : tenantId || sessionStorage.getItem("selectedTenantId");
  };

  // Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      const tenantFilterID = getTenantFilterID();
      if (!tenantFilterID) return;

      const q = query(
        collection(firestore, "Users"),
        where("Is_Active", "==", true),
        where("tenantId", "==", tenantFilterID)
      );

      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => doc.data().userEmail);
      setUserList(users);
    };
    fetchUsers();
  }, [userRole, tenantId, selectedTenantId]);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      const tenantFilterID = getTenantFilterID();
      if (!tenantFilterID) return;

      try {
        const teamsList = await getTeamsForTenant(tenantFilterID);
        setTeams(teamsList);
      } catch (error) {
        console.error("Error fetching teams:", error);
        setErrorMessage("Failed to fetch teams");
      }
    };
    fetchTeams();
  }, [userRole, tenantId, selectedTenantId, activeTab]);

  // Handle user selection for create form
  const handleUserSelect = (event) => {
    const value = event.target.value;
    if (value && !selectedUsers.includes(value)) {
      setSelectedUsers([...selectedUsers, value]);
      setUserList(userList.filter((user) => user !== value));
    }
  };

  // Handle user removal from create form
  const handleRemoveUser = (userToRemove) => {
    setSelectedUsers(selectedUsers.filter((user) => user !== userToRemove));
    setUserList([...userList, userToRemove]);
  };

  // Handle user selection for edit form
  const handleEditUserSelect = (event) => {
    const value = event.target.value;
    if (value && !editSelectedUsers.includes(value)) {
      setEditSelectedUsers([...editSelectedUsers, value]);
    }
  };

  // Handle user removal from edit form
  const handleRemoveEditUser = (userToRemove) => {
    setEditSelectedUsers(editSelectedUsers.filter((user) => user !== userToRemove));
  };

  // Get current location coordinates
  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setLatitude(position.coords.latitude.toString());
        setLongitude(position.coords.longitude.toString());
      });
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  //for distributed
  const getUserLocation = (user) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((position) => {
      setMemberCoordinates((prev) => ({
        ...prev,
        [user]: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          radius: prev[user]?.radius || 100,
        },
      }));
    });
  } else {
    alert("Geolocation is not supported by this browser.");
  }
};

  // Get location for edit form
  const getEditLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setEditLatitude(position.coords.latitude.toString());
        setEditLongitude(position.coords.longitude.toString());
      });
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  // Handle create form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!teamName.trim()) {
      setErrorMessage("Team name is required");
      return;
    }
    if (selectedUsers.length === 0) {
      setErrorMessage("Please select at least one user");
      return;
    }

    if (teamType === "colocated") {
      if (!latitude || !longitude  || !radius) {
        setErrorMessage("Coordinates and radius are required for colocated team");
        return;
      }
    }

    if (teamType === "distributed") {
      const allHaveCoords = selectedUsers.every(
        (user) =>
          memberCoordinates[user]?.latitude &&
          memberCoordinates[user]?.longitude &&
          memberCoordinates[user]?.radius
      );
    
      if (!allHaveCoords) {
        setErrorMessage("All members must have coordinates and radius");
        return;
      }
    }

    const tenantInfoID = getTenantFilterID();
    if (!tenantInfoID) {
      setErrorMessage("Tenant ID not available");
      return;
    }

    let groupInfo;

    groupInfo = {
      members: selectedUsers,
      teamType: teamType,
      teamName: teamName.trim(),
      tenantId: tenantInfoID,
    };

    if (teamType === "colocated") {
      const coords = {
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        radius: parseFloat(radius),
      };
    
      groupInfo.coordinates = coords;
    
    }   

    if (teamType === "distributed") {
      groupInfo.memberCoordinates = {};
    
      selectedUsers.forEach((user) => {
        if (memberCoordinates[user]) {
          groupInfo.memberCoordinates[user] = {
            latitude: parseFloat(memberCoordinates[user].latitude),
            longitude: parseFloat(memberCoordinates[user].longitude),
            radius: parseFloat(memberCoordinates[user].radius),
          };
        }
      });
    }

    try {
      const docRef = await addDoc(collection(firestore, "Groups"), groupInfo);
      const teamId = docRef.id;
      // Update coordinates in Users collection
      await Promise.all(
        selectedUsers.map((userEmail) => {
          const userRef = doc(firestore, "Users", userEmail);
          return updateDoc(userRef, {
            teamIds: arrayUnion(teamId),
          });
        })
      );

      setSuccessMessage("Team created successfully!");
      setErrorMessage("");
      
      // Reset the form
      setSelectedUsers([]);
      setLatitude("");
      setLongitude("");
      setTeamName("");
      
      // Refresh teams list
      const teamsList = await getTeamsForTenant(tenantInfoID);
      setTeams(teamsList);
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error adding group: ", error.message);
      setErrorMessage("Failed to create team: " + error.message);
    }
  };

  // Handle edit button click
  const handleEditClick = (team) => {
    setEditingTeam(team);
    setEditTeamName(team.teamName || "");
    setEditRadius(team.coordinates?.radius?.toString() || "");
    setEditSelectedUsers(team.members || []);
    setEditSupervisorEmail(team.supervisorEmail || "");

    // ✅ ADD THIS
    setEditTeamType(team.teamType || "colocated");

    if (team.teamType === "colocated") {
      setEditLatitude(team.coordinates?.latitude?.toString() || "");
      setEditLongitude(team.coordinates?.longitude?.toString() || "");
      setEditMemberCoordinates({});
    } else {
      const formattedCoords = {};

      Object.keys(team.memberCoordinates || {}).forEach((user) => {
        formattedCoords[user] = {
          latitude: team.memberCoordinates[user].latitude?.toString() || "",
          longitude: team.memberCoordinates[user].longitude?.toString() || "",
          radius: team.memberCoordinates[user].radius?.toString() || "",
        };
      });

      setEditMemberCoordinates(formattedCoords);
      setEditLatitude("");
      setEditLongitude("");
    }

    setEditDialogOpen(true);
  };

  // Handle update form submission
  const handleUpdateSubmit = async () => {

    const oldMembers = editingTeam.members || [];

    for (const userEmail of oldMembers) {
      if (!editSelectedUsers.includes(userEmail)) {
        const userRef = doc(firestore, "Users", userEmail);

        await updateDoc(userRef, {
          teamIds: arrayRemove(editingTeam.id),
        });
      }
    }

    if (!editTeamName.trim()) {
      setErrorMessage("Team name is required");
      return;
    }
    if (editSelectedUsers.length === 0) {
      setErrorMessage("Please select at least one user");
      return;
    }

    const tenantInfoID = getTenantFilterID();
    if (!tenantInfoID) {
      setErrorMessage("Tenant ID not available");
      return;
    }
    const updates = {
      teamName: editTeamName.trim(),
      members: editSelectedUsers,
      teamType: editTeamType,
    };

    if (editTeamType === "colocated") {
      updates.memberCoordinates = {}; // clear old distributed data
      if (!editLatitude || !editLongitude || !editRadius) {
        setErrorMessage("Coordinates and radius are required for colocated team");
        return;
      }
    }

    if (editTeamType === "distributed") {
      const allHaveCoords = editSelectedUsers.every(
        (user) =>
          editMemberCoordinates[user]?.latitude &&
          editMemberCoordinates[user]?.longitude &&
          editMemberCoordinates[user]?.radius
      );

      if (!allHaveCoords) {
        setErrorMessage("All members must have coordinates");
        return;
      }
    }

    try {

      if (editTeamType === "colocated") {
        const coords = {
          latitude: parseFloat(editLatitude),
          longitude: parseFloat(editLongitude),
          radius: parseFloat(editRadius),
        };
      
        updates.coordinates = coords;
    
      }
      
      if (editTeamType === "distributed") {
        updates.memberCoordinates = {};
      
        editSelectedUsers.forEach((user) => {
          if (editMemberCoordinates[user]) {
            updates.memberCoordinates[user] = {
              latitude: parseFloat(editMemberCoordinates[user].latitude),
              longitude: parseFloat(editMemberCoordinates[user].longitude),
              radius: parseFloat(editMemberCoordinates[user].radius),
            };
          }
        });
      }

      if (editSupervisorEmail) {
        updates.supervisorEmail = editSupervisorEmail.trim();
      }

      await updateTeam(editingTeam.id, updates);

      // ADD/UPDATE new users
      await Promise.all(
        editSelectedUsers.map((userEmail) => {
          const userRef = doc(firestore, "Users", userEmail);
          return updateDoc(userRef, {
            teamIds: arrayUnion(editingTeam.id),
          });
        })
      );

      setSuccessMessage("Team updated successfully!");
      setErrorMessage("");
      setEditDialogOpen(false);
      setEditingTeam(null);
      
      // Refresh teams list
      const teamsList = await getTeamsForTenant(tenantInfoID);
      setTeams(teamsList);
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating team:", error);
      setErrorMessage("Failed to update team: " + error.message);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (team) => {
    setTeamToDelete(team);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!teamToDelete) return;

    try {
      await Promise.all(
        (teamToDelete.members || []).map((userEmail) => {
          const userRef = doc(firestore, "Users", userEmail);
          return updateDoc(userRef, {
            teamIds: arrayRemove(teamToDelete.id),
          });
        })
      );
      await deleteTeam(teamToDelete.id);
      setSuccessMessage("Team deleted successfully!");
      setErrorMessage("");
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
      
      // Refresh teams list
      const tenantInfoID = getTenantFilterID();
      const teamsList = await getTeamsForTenant(tenantInfoID);
      setTeams(teamsList);
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting team:", error);
      setErrorMessage("Failed to delete team: " + error.message);
      setDeleteDialogOpen(false);
    }
  };

  // Handle duplicate button click
  const handleDuplicateClick = (team) => {
    setSelectedTeamId(team.id);
    setNewSupervisorEmail("");
    setDuplicateError("");
    setDuplicateSuccess("");
    setDuplicateDialogOpen(true);
  };

  // Handle team duplication
  const handleDuplicateTeam = async () => {
    if (!selectedTeamId || !newSupervisorEmail.trim()) {
      setDuplicateError("Please enter supervisor email");
      return;
    }

    const tenantFilterID = getTenantFilterID();
    if (!tenantFilterID) {
      setDuplicateError("No tenant ID available");
      return;
    }

    try {
      await duplicateTeam(selectedTeamId, newSupervisorEmail.trim(), tenantFilterID);
      setDuplicateSuccess("Team duplicated successfully!");
      setDuplicateError("");
      setSelectedTeamId("");
      setNewSupervisorEmail("");
      
      // Refresh teams list
      const teamsList = await getTeamsForTenant(tenantFilterID);
      setTeams(teamsList);
      
      setTimeout(() => {
        setDuplicateDialogOpen(false);
        setDuplicateSuccess("");
      }, 2000);
    } catch (error) {
      console.error("Error duplicating team:", error);
      setDuplicateError("Failed to duplicate team: " + error.message);
    }
  };

  // Get available users for edit (users not in current team)
  const getAvailableUsersForEdit = () => {
    const allUsers = [...userList];
    editSelectedUsers.forEach(email => {
      if (!allUsers.includes(email)) {
        allUsers.push(email);
      }
    });
    return allUsers.filter(user => !editSelectedUsers.includes(user));
  };

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 50%, rgb(231, 58, 237) 100%)",
      display: "flex",
      flexDirection: "column",
    }}>
      <PageNavBar title="Team Management" backTo="/dashboard" />
      <TenantBanner />

      <Box sx={{ flex: 1, px: 2, py: 3 }}>
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Create Team" />
            <Tab label="Update Team" />
          </Tabs>

          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ m: 2 }}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ m: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {activeTab === 0 && (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
              <Stack spacing={3}>
                <TextField
                  label="Team Name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  fullWidth
                  required
                />

                <FormControl fullWidth>
                    <InputLabel>Team Type</InputLabel>
                    <Select
                      value={teamType}
                      label="Team Type"
                      onChange={(e) => setTeamType(e.target.value)}
                    >
                      <MenuItem value="colocated">Co-Located</MenuItem>
                      <MenuItem value="distributed">Distributed</MenuItem>
                    </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Select Users</InputLabel>
                  <Select value="" label="Select Users" onChange={handleUserSelect}>
                    {userList.map((user) => (
                      <MenuItem key={user} value={user}>
                        {user}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {selectedUsers.map((user) => (
                    <Chip
                      key={user}
                      label={user}
                      onDelete={() => handleRemoveUser(user)}
                      color="primary"
                    />
                  ))}
                </Stack>

                {teamType === "colocated" && (
                  <>
                    <TextField
                      label="Latitude"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      fullWidth
                      type="number"
                    />

                    <TextField
                      label="Longitude"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      fullWidth
                      type="number"
                    />

                    <TextField
                      label="Radius (in meters)"
                      value={radius}
                      onChange={(e) => setRadius(e.target.value)}
                      fullWidth
                      type="number"
                    />

                    <Button variant="outlined" onClick={getLocation}>
                      Get Current Location
                    </Button>
                  </>
                )}

                {teamType === "distributed" && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">
                      Assign Coordinates for Each Member
                    </Typography>

                    {selectedUsers.map((user) => (
                      <Card key={user}>
                        <CardContent>
                          <Typography>{user}</Typography>

                          <Stack direction="row" spacing={2} mt={1}>
                            <TextField
                              label="Latitude"
                              type="number"
                              value={memberCoordinates[user]?.latitude || ""}
                              onChange={(e) =>
                                setMemberCoordinates({
                                  ...memberCoordinates,
                                  [user]: {
                                    ...memberCoordinates[user],
                                    latitude: e.target.value,
                                  },
                                })
                              }
                            />

                            <TextField
                              label="Longitude"
                              type="number"
                              value={memberCoordinates[user]?.longitude || ""}
                              onChange={(e) =>
                                setMemberCoordinates({
                                  ...memberCoordinates,
                                  [user]: {
                                    ...memberCoordinates[user],
                                    longitude: e.target.value,
                                  },
                                })
                              }
                            />

                            <TextField
                              label="Radius (in meters)"
                              type="number"
                              value={memberCoordinates[user]?.radius || ""}
                              onChange={(e) =>
                                setMemberCoordinates({
                                  ...memberCoordinates,
                                  [user]: {
                                    ...memberCoordinates[user],
                                    radius: e.target.value,
                                  },
                                })
                              }
                            />
                          </Stack>
                            
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => getUserLocation(user)}
                          >
                            Get Current Location
                          </Button>
                            
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}

                <Button variant="contained" color="primary" type="submit">
                  Create Team
                </Button>
              </Stack>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              {teams.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No teams found. Create a team first.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Team Name</strong></TableCell>
                        <TableCell><strong>Members</strong></TableCell>
                        <TableCell><strong>Supervisor</strong></TableCell>
                        <TableCell><strong>Team Type</strong></TableCell>
                        {/* <TableCell><strong>Coordinates</strong></TableCell> */}
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teams.map((team) => (
                        <TableRow key={team.id}>
                          <TableCell>{team.teamName || `Team ${team.id}`}</TableCell>
                          <TableCell>
                            {team.members && team.members.length > 0
                              ? `${team.members.length} member(s)`
                              : "No members"}
                          </TableCell>
                          <TableCell>{team.supervisorEmail || "None"}</TableCell>
                          <TableCell>{team.teamType || "colocated"}</TableCell>
                          {/* <TableCell>
                            {team.teamType === "colocated" ? (
                              team.coordinates?.latitude != null && team.coordinates?.longitude != null
                                ? `${Number(team.coordinates.latitude).toFixed(4)}, ${Number(team.coordinates.longitude).toFixed(4)}`
                                : "Not set"
                            ) : team.memberCoordinates ? (
                              `${Object.keys(team.memberCoordinates).length} locations`
                            ) : (
                              "Not set"
                            )}
                          </TableCell> */}
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Edit Team">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(team)}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Team">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteClick(team)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Duplicate Team">
                                <IconButton
                                  color="secondary"
                                  onClick={() => handleDuplicateClick(team)}
                                  size="small"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Edit Team Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingTeam(null);
          setErrorMessage("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Team Name"
              value={editTeamName}
              onChange={(e) => setEditTeamName(e.target.value)}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>Team Type</InputLabel>
              <Select
                value={editTeamType}
                label="Team Type"
                onChange={(e) => {
                  const newType = e.target.value;
                  setEditTeamType(newType);
                
                  // Reset when switching
                  if (newType === "colocated") {
                    const firstUser = editSelectedUsers[0];

                    if (firstUser && editMemberCoordinates[firstUser]) {
                      setEditLatitude(editMemberCoordinates[firstUser].latitude || "");
                      setEditLongitude(editMemberCoordinates[firstUser].longitude || "");
                      setEditRadius(editMemberCoordinates[firstUser].radius || "");
                    }
                  
                    setEditMemberCoordinates({});
                  } else {
                    setEditLatitude("");
                    setEditLongitude("");
                  }
                }}
              >
                <MenuItem value="colocated">Co-Located</MenuItem>
                <MenuItem value="distributed">Distributed</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Add Users</InputLabel>
              <Select value="" label="Add Users" onChange={handleEditUserSelect}>
                {getAvailableUsersForEdit().map((user) => (
                  <MenuItem key={user} value={user}>
                    {user}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {editSelectedUsers.map((user) => (
                <Chip
                  key={user}
                  label={user}
                  onDelete={() => handleRemoveEditUser(user)}
                  color="primary"
                />
              ))}
            </Stack>

            <TextField
              label="Supervisor Email"
              value={editSupervisorEmail}
              onChange={(e) => setEditSupervisorEmail(e.target.value)}
              fullWidth
              placeholder="Enter supervisor email (optional)"
            />

            {editTeamType === "colocated" && (
              <>
                <TextField
                  label="Latitude"
                  value={editLatitude}
                  onChange={(e) => setEditLatitude(e.target.value)}
                  fullWidth
                  type="number"
                />

                <TextField
                  label="Longitude"
                  value={editLongitude}
                  onChange={(e) => setEditLongitude(e.target.value)}
                  fullWidth
                  type="number"
                />

                <TextField
                  label="Radius (in meters)"
                  value={editRadius}
                  onChange={(e) => setEditRadius(e.target.value)}
                  fullWidth
                  type="number"
                />

                <Button variant="outlined" onClick={getEditLocation}>
                  Get Current Location
                </Button>
              </>
            )}

            {editTeamType === "distributed" && (
              <Stack spacing={2}>
                <Typography variant="subtitle1">
                  Assign Coordinates for Each Member
                </Typography>
            
                {editSelectedUsers.map((user) => (
                  <Card key={user}>
                    <CardContent>
                      <Typography>{user}</Typography>
                
                      <Stack direction="row" spacing={2} mt={1}>
                        <TextField
                          label="Latitude"
                          type="number"
                          value={editMemberCoordinates[user]?.latitude || ""}
                          onChange={(e) =>
                            setEditMemberCoordinates({
                              ...editMemberCoordinates,
                              [user]: {
                                ...editMemberCoordinates[user],
                                latitude: e.target.value,
                              },
                            })
                          }
                        />

                        <TextField
                          label="Longitude"
                          type="number"
                          value={editMemberCoordinates[user]?.longitude || ""}
                          onChange={(e) =>
                            setEditMemberCoordinates({
                              ...editMemberCoordinates,
                              [user]: {
                                ...editMemberCoordinates[user],
                                longitude: e.target.value,
                              },
                            })
                          }
                        />

                        <TextField
                          label="Radius (in meters)"
                          type="number"
                          value={editMemberCoordinates[user]?.radius || ""}
                          onChange={(e) =>
                            setEditMemberCoordinates({
                              ...editMemberCoordinates,
                              [user]: {
                                ...editMemberCoordinates[user],
                                radius: e.target.value,
                              },
                            })
                          }
                        />
                      </Stack>
                        
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={() => {
                          navigator.geolocation.getCurrentPosition((position) => {
                            setEditMemberCoordinates((prev) => ({
                              ...prev,
                              [user]: {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                                radius: prev[user]?.radius || 100,
                              },
                            }));
                          });
                        }}
                      >
                        Get Current Location
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingTeam(null);
            setErrorMessage("");
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdateSubmit}>
            Update Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTeamToDelete(null);
        }}
      >
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{teamToDelete?.teamName || `Team ${teamToDelete?.id}`}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setTeamToDelete(null);
          }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Team Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setDuplicateError("");
          setDuplicateSuccess("");
          setSelectedTeamId("");
          setNewSupervisorEmail("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Team</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {duplicateError && (
              <Alert severity="error" onClose={() => setDuplicateError("")}>
                {duplicateError}
              </Alert>
            )}
            {duplicateSuccess && (
              <Alert severity="success" onClose={() => setDuplicateSuccess("")}>
                {duplicateSuccess}
              </Alert>
            )}
            <TextField
              label="New Supervisor Email"
              value={newSupervisorEmail}
              onChange={(e) => setNewSupervisorEmail(e.target.value)}
              fullWidth
              placeholder="Enter email of the new supervisor"
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDuplicateDialogOpen(false);
              setDuplicateError("");
              setDuplicateSuccess("");
              setSelectedTeamId("");
              setNewSupervisorEmail("");
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDuplicateTeam}>
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default FormTeam;
